import { forwardRef } from "react";
import { mediaUrl } from "@/lib/api";
import { useStore } from "@/store/useStore";
import { cn } from "@/lib/utils";

export const ThumbnailPreview = forwardRef(function ThumbnailPreview({
  match,
  showCompetition,
  showDateTime,
  showVenue,
  showTeamNames = true,
  logoSize,
  platform,
  brand,
}, ref) {
  const { teams, leagues } = useStore();

  const team1 = teams.find((t) => t.id === match?.team1Id);
  const team2 = teams.find((t) => t.id === match?.team2Id);
  const league = leagues.find((l) => l.id === match?.leagueId);

  const team1Name = team1?.name || match?.team1Name || "Team 1";
  const team2Name = team2?.name || match?.team2Name || "Team 2";
  const isTBD = team1Name === "TBD" && team2Name === "TBD";
  
  // Check if this is a competition-only thumbnail (no teams, just league)
  const isCompetitionOnly = isTBD && league;

  // Determine aspect ratio based on platform
  const isVertical = platform === "uscreen-vertical";
  const aspectRatio = isVertical ? "1188/1682" : (platform === "uscreen" ? "1480/840" : "16/9");

  // Get colors - for competition only, use league colors for gradient
  const leftColor = isCompetitionOnly 
    ? (league?.primaryColor || "#1e3a5f")
    : (team1?.primaryColor || league?.primaryColor || "#1e3a5f");
  const rightColor = isCompetitionOnly 
    ? (league?.secondaryColor || league?.primaryColor || "#0ea5e9")
    : (team2?.primaryColor || league?.secondaryColor || "#0ea5e9");

  if (!match) {
    return (
      <div
        className={cn(
          "rounded-xl overflow-hidden border border-border",
          "flex items-center justify-center"
        )}
        style={{
          aspectRatio,
          background: "linear-gradient(135deg, hsl(213 25% 17%) 0%, hsl(213 25% 14%) 100%)",
        }}
      >
        <div className="text-center p-8">
          <div className={cn(
            "rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4",
            isVertical ? "w-16 h-16" : "w-20 h-20"
          )}>
            <svg
              className={cn("text-muted-foreground", isVertical ? "w-8 h-8" : "w-10 h-10")}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <p className={cn("text-foreground font-medium", isVertical && "text-sm")}>Select a Match</p>
          <p className={cn("text-muted-foreground mt-1", isVertical ? "text-xs" : "text-sm")}>
            Choose a match to preview
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cn("rounded-xl overflow-hidden border border-border relative thumbnail-capture")}
      style={{
        aspectRatio,
      }}
    >
      {/* Gradient Background */}
      <div
        className="absolute inset-0"
        style={{
          background: isVertical 
            ? `linear-gradient(180deg, ${leftColor} 0%, ${leftColor}dd 40%, ${rightColor}dd 60%, ${rightColor} 100%)`
            : `linear-gradient(90deg, ${leftColor} 0%, ${leftColor}dd 35%, ${rightColor}dd 65%, ${rightColor} 100%)`,
        }}
      />

      {/* Subtle pattern overlay */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: isVertical
            ? `radial-gradient(circle at 50% 25%, white 0%, transparent 50%),
               radial-gradient(circle at 50% 75%, white 0%, transparent 50%)`
            : `radial-gradient(circle at 20% 50%, white 0%, transparent 50%),
               radial-gradient(circle at 80% 50%, white 0%, transparent 50%)`,
        }}
      />

      {/* Content */}
      <div className="relative h-full flex flex-col">
        {/* Top Bar - Competition & Date */}
        {showCompetition && match.competition && (
          <div className={cn("absolute left-3", isVertical ? "top-4" : "top-3")}>
            <div className={cn(
              "px-3 py-1 rounded bg-black/40 backdrop-blur-sm font-medium text-white",
              isVertical ? "text-[10px]" : "text-xs"
            )}>
              {match.competition}
            </div>
          </div>
        )}

        {showDateTime && match.date && (
          <div className={cn("absolute right-3", isVertical ? "top-4" : "top-3")}>
            <div className="text-right">
              <p className={cn("font-medium text-white/90", isVertical ? "text-[10px]" : "text-xs")}>
                {new Date(match.date).toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}
              </p>
              {match.time && (
                <p className={cn("font-bold text-white", isVertical ? "text-xs" : "text-sm")}>
                  {match.time}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Main Content - Logos */}
        {isCompetitionOnly ? (
          /* Competition Only Layout - Single Large League Logo */
          <div 
            className="absolute flex items-center justify-center"
            style={{
              top: 0,
              left: 0,
              right: 0,
              bottom: '15%', // Account for branding at bottom
            }}
          >
            <div
              className="flex items-center justify-center"
              style={{
                width: `${logoSize * 3.5}px`,
                height: `${logoSize * 3.5}px`,
              }}
            >
              {league?.logo ? (
                <img
                  src={mediaUrl(league.logo)}
                  alt={league.name}
                  className="w-full h-full object-contain drop-shadow-lg"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <span className="text-6xl font-bold text-white drop-shadow-lg">
                  {league?.name?.[0] || "?"}
                </span>
              )}
            </div>
          </div>
        ) : isVertical ? (
          /* Vertical Layout - Stacked */
          <div className="flex-1 flex flex-col items-center justify-center px-4 py-6">
            {/* Team 1 Logo */}
            <div
              className="flex items-center justify-center overflow-hidden"
              style={{
                width: `${logoSize * 2.4}px`,
                height: `${logoSize * 2.4}px`,
              }}
            >
              {team1?.logo ? (
                <img
                  src={mediaUrl(team1.logo)}
                  alt={team1Name}
                  className="w-full h-full object-contain drop-shadow-lg"
                  loading="lazy"
                  decoding="async"
                />
              ) : isTBD ? (
                <span className="text-4xl font-bold text-white/60 drop-shadow-lg">?</span>
              ) : (
                <span className="text-5xl font-bold text-white drop-shadow-lg">
                  {team1Name[0]}
                </span>
              )}
            </div>

            {/* League Logo in Center */}
            <div className="my-3">
              <div
                className="flex items-center justify-center overflow-hidden"
                style={{
                  width: `${logoSize * 0.625}px`,
                  height: `${logoSize * 0.625}px`,
                }}
              >
                {league?.logo ? (
                  <img
                    src={mediaUrl(league.logo)}
                    alt={league.name}
                    className="w-full h-full object-contain drop-shadow-lg"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <svg
                    className="w-6 h-6 text-white/80 drop-shadow-lg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                    />
                  </svg>
                )}
              </div>
            </div>

            {/* Team 2 Logo */}
            <div
              className="flex items-center justify-center overflow-hidden"
              style={{
                width: `${logoSize * 2.4}px`,
                height: `${logoSize * 2.4}px`,
              }}
            >
              {team2?.logo ? (
                <img
                  src={mediaUrl(team2.logo)}
                  alt={team2Name}
                  className="w-full h-full object-contain drop-shadow-lg"
                  loading="lazy"
                  decoding="async"
                />
              ) : isTBD ? (
                <span className="text-4xl font-bold text-white/60 drop-shadow-lg">?</span>
              ) : (
                <span className="text-5xl font-bold text-white drop-shadow-lg">
                  {team2Name[0]}
                </span>
              )}
            </div>
          </div>
        ) : (
          /* Horizontal Layout - Side by Side */
          <div className="flex-1 flex items-center justify-center px-4 lg:px-8">
            {/* Team 1 Logo */}
            <div className="flex-1 flex justify-center">
              <div
                className="flex items-center justify-center overflow-hidden"
                style={{
                  width: `${logoSize * 3}px`,
                  height: `${logoSize * 3}px`,
                }}
              >
                {team1?.logo ? (
                  <img
                    src={mediaUrl(team1.logo)}
                    alt={team1Name}
                    className="w-full h-full object-contain drop-shadow-lg"
                    loading="lazy"
                    decoding="async"
                  />
                ) : isTBD ? (
                  <span className="text-5xl font-bold text-white/60 drop-shadow-lg">?</span>
                ) : (
                  <span className="text-6xl font-bold text-white drop-shadow-lg">
                    {team1Name[0]}
                  </span>
                )}
              </div>
            </div>

            {/* League Logo in Center */}
            <div className="flex flex-col items-center mx-4">
              <div
                className="flex items-center justify-center overflow-hidden"
                style={{
                  width: `${logoSize * 0.875}px`,
                  height: `${logoSize * 0.875}px`,
                }}
              >
                {league?.logo ? (
                  <img
                    src={mediaUrl(league.logo)}
                    alt={league.name}
                    className="w-full h-full object-contain drop-shadow-lg"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <svg
                    className="w-10 h-10 text-white/80 drop-shadow-lg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                    />
                  </svg>
                )}
              </div>
            </div>

            {/* Team 2 Logo */}
            <div className="flex-1 flex justify-center">
              <div
                className="flex items-center justify-center overflow-hidden"
                style={{
                  width: `${logoSize * 3}px`,
                  height: `${logoSize * 3}px`,
                }}
              >
                {team2?.logo ? (
                  <img
                    src={mediaUrl(team2.logo)}
                    alt={team2Name}
                    className="w-full h-full object-contain drop-shadow-lg"
                    loading="lazy"
                    decoding="async"
                  />
                ) : isTBD ? (
                  <span className="text-5xl font-bold text-white/60 drop-shadow-lg">?</span>
                ) : (
                  <span className="text-6xl font-bold text-white drop-shadow-lg">
                    {team2Name[0]}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bottom Bar - Team Names & Venue */}
        {(showTeamNames || showVenue) && (
          <div className={cn("px-4", isVertical ? "pb-16" : "lg:px-8")} style={{ paddingBottom: isVertical ? '18%' : '12%' }}>
            {showTeamNames && (
              <div className={cn(
                "flex items-center",
                isVertical ? "flex-col gap-1 text-center" : "justify-between"
              )}>
                {isVertical ? (
                  <>
                    <p className="text-xs font-semibold text-white truncate">
                      {team1Name}
                    </p>
                    <span className="text-[10px] font-medium text-white/60 uppercase tracking-wider">vs</span>
                    <p className="text-xs font-semibold text-white truncate">
                      {team2Name}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex-1">
                      <p className="text-sm lg:text-base font-semibold text-white truncate">
                        {team1Name}
                      </p>
                    </div>
                    <div className="px-4">
                      <span className="text-xs font-medium text-white/60 uppercase tracking-wider">vs</span>
                    </div>
                    <div className="flex-1 text-right">
                      <p className="text-sm lg:text-base font-semibold text-white truncate">
                        {team2Name}
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}
            {showVenue && match.venue && (
              <p className={cn("text-white/70 text-center mt-2", isVertical ? "text-[10px]" : "text-xs")}>
                📍 {match.venue}
              </p>
            )}
          </div>
        )}

        {/* Permanent Branding - configurable per Brand */}
        <div
          className="absolute left-0 right-0 text-center flex flex-col items-center justify-center"
          style={{ bottom: '7%' }}
        >
          {brand?.logo ? (
            <>
              {brand.showWatchOnText !== false && (
                <p
                  className="text-white drop-shadow-lg"
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 900,
                    fontSize: isVertical ? '0.5rem' : '0.75rem',
                    lineHeight: 1.2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: isVertical ? '4px' : '6px',
                  }}
                >
                  Watch on
                </p>
              )}
              <img
                src={mediaUrl(brand.logo)}
                alt={brand.name || 'Brand'}
                style={{
                  height: isVertical ? '6%' : '11%',
                  maxWidth: '60%',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                }}
              />
            </>
          ) : (
            <p
              className="text-white drop-shadow-lg"
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 900,
                fontSize: isVertical ? '0.5rem' : '0.75rem',
                lineHeight: 1.2,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {brand?.showWatchOnText !== false && (
                <>
                  Watch on
                  <br />
                </>
              )}
              <span style={{ fontSize: isVertical ? '0.65rem' : '1rem' }}>
                {brand?.name || 'New Logical Sports'}
              </span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
});
